/**
 * Redux store.
 *
 * Matches the template convention: rootReducer composed in its own
 * file, this file just calls configureStore + setupListeners.
 *
 * setupListeners enables RTK Query's refetchOnFocus / refetchOnReconnect
 * behavior across the app - without it, those settings are silently
 * ignored. The template includes it; we previously omitted it.
 */
import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { baseApi } from "@/app/store/api/baseApi";
import { authCacheListener } from "@/app/store/authCacheListener";
import { rootReducer } from "@/app/store/rootReducer";

/** The configured Redux store. */
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      // The auth-cache listener must run BEFORE baseApi.middleware so a
      // resetApiState() dispatched in its effect is processed cleanly.
      .prepend(authCacheListener.middleware)
      .concat(baseApi.middleware),
  devTools: import.meta.env.DEV,
});

// Enable refetchOnFocus / refetchOnReconnect on RTK Query endpoints
// that opt in. No-op for endpoints that don't.
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
